/* eslint-disable @typescript-eslint/no-non-null-assertion */
import "react-circular-progressbar/dist/styles.css";
import "react-multi-carousel/lib/styles.css";
import "react-toastify/dist/ReactToastify.css";

import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
import {
  buildStyles,
  CircularProgressbarWithChildren,
} from "react-circular-progressbar";
import { BsChevronDown, BsChevronUp } from "react-icons/bs";
import { ButtonGroupProps } from "react-multi-carousel";
import { toast } from "react-toastify";
import { PersistGate } from "redux-persist/integration/react";

import { generateAdeylaPurchase } from "../../../api/account/theClub/generateAdelyaPurchase";
import { LoyaltyCategory } from "../../../api/types/account.type";
import {
  Formula,
  RestaurantItemType,
} from "../../../api/types/restaurant.type";
import { translationsContent } from "../../../localization/translations";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  addItemToCart,
  addNewFormulaToCart,
  removeFormulaFromCart,
  removeItemFromCart,
  selectCart,
} from "../../../redux/slices/cartSlice";
import { persistor } from "../../../redux/store";
import { useLanguage } from "../../../store/lang.context";
import { useLoader } from "../../../store/loader.context";
import { useUser } from "../../../store/user.context";
import { translate } from "../../../utils/translate";
import Button from "../../ui/button/SimpleButton";
import classes from "./eatfidCardContainer.module.scss";

const CompositionModal = dynamic(() => import("../../menupage/composition"), {
  ssr: false,
});
const Carousel = dynamic(() => import("react-multi-carousel"), { ssr: false });
const IngredientCard = dynamic(() => import("../../ui/cards/IngredientCard"), {
  ssr: false,
});
const FormulaCard = dynamic(() => import("../../ui/cards/FormulaCard"), {
  ssr: false,
});
const DynamicModal = dynamic(() => import("../../ui/modal/DynamicModal"), {
  ssr: false,
});

interface Props {
  points: number;
  restaurant?: RestaurantItemType;
  categories: LoyaltyCategory[];
  userPoints: number;
  alternativeLook?: boolean;
  alternativeDropdown?: boolean;
  outsideDisplayContent?: boolean;
  showcase?: boolean;
  showBone?: boolean;
  onClick?: () => void;
}

const ButtonGroup = ({ next, previous, ...rest }: ButtonGroupProps) => {
  const currentSlide = rest.carouselState!.currentSlide;
  const totalItems = rest.carouselState!.totalItems;
  const slidesToShow = rest.carouselState!.slidesToShow;

  return (
    <div className={`carousel-button-group ${classes.buttonGroup}`}>
      <button
        aria-label="Go to previous slide"
        className={`${
          currentSlide === 0
            ? classes.disable
            : "react-multiple-carousel__arrow react-multiple-carousel__arrow--left"
        } ${classes.outsideButton}`}
        onClick={() => {
          if (previous) previous();
        }}
      />
      <button
        aria-label="Go to next slide"
        className={`${
          currentSlide === totalItems - slidesToShow
            ? classes.disable
            : "react-multiple-carousel__arrow react-multiple-carousel__arrow--right"
        } ${classes.outsideButton}`}
        onClick={() => {
          if (next) next();
        }}
      />
    </div>
  );
};

const carouselResponsive = {
  superLargeDesktop: {
    breakpoint: { max: 4000, min: 1300 },
    items: 3,
  },
  desktop: {
    breakpoint: { max: 1300, min: 700 },
    items: 2,
  },
  tablet: {
    breakpoint: { max: 700, min: 500 },
    items: 1,
  },
  mobile: {
    breakpoint: { max: 500, min: 0 },
    items: 1,
  },
};

const EatfidCardContainer = ({
  points,
  categories,
  userPoints,
  restaurant,
  alternativeLook,
  alternativeDropdown = false,
  outsideDisplayContent = undefined,
  showcase = false,
  showBone = true,
  onClick,
}: Props) => {
  const [displayContent, setDisplayContent] = useState<boolean>(false);
  const [customFormulaModalVisible, setCustomFormulaModalVisible] =
    useState<boolean>(false);
  const [adelyaPurchaseModalVisible, setAdelyaPurchaseModalVisible] =
    useState<boolean>(false);
  const [adelyaPurchaseMessage, setAdelyaPurchaseMessage] =
    useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<number>(0);
  const [selectedFormula, setSelectedFormula] = useState<Formula>();
  const [id, setId] = useState<number>(-1);

  const { language } = useLanguage();
  const { setLoader } = useLoader();
  const { user, setUser } = useUser();

  const dispatch = useAppDispatch();
  const cart = useAppSelector(selectCart);

  const percentage = Math.min((userPoints / points) * 100, 100);

  const handleClickFunction = onClick || (() => {});

  const handleCustomSaladModalClose = () => {
    setCustomFormulaModalVisible(false);
  };

  const notify = () =>
    toast.success("Article ajouté avec succès", {
      position: "top-right",
      autoClose: 1500,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });

  const purschaseEatfidInstantly = async (
    trivec_code: string,
    points: number
  ) => {
    setLoader(true);
    const res = await generateAdeylaPurchase(+trivec_code);
    setLoader(false);
    setAdelyaPurchaseModalVisible(true);
    if (res.success) {
      if (user?.id && user.eatfid_points != null) {
        setUser((prev) =>
          prev
            ? {
                ...prev,
                eatfid_points: `${
                  (user.eatfid_points ? +user.eatfid_points : 0) - points
                }`,
                left_points: Math.max(
                  +(prev.eatfid_points ?? 0) -
                    (cart.eatfid_points_cost ?? 0) -
                    points,
                  0
                ),
              }
            : prev
        );
      }
      setAdelyaPurchaseMessage(
        "Votre bon a été commandé avec succès. Vous allez le recevoir par email."
      );
    } else {
      setAdelyaPurchaseMessage(
        "Une erreur est servenue. Merci de réessayer ultérieurement."
      );
    }
  };

  useEffect(() => {
    if (outsideDisplayContent !== undefined)
      setDisplayContent(outsideDisplayContent);
  }, [outsideDisplayContent]);

  useEffect(() => {
    if (categories[0].formulas) {
      categories[0].formulas.forEach((formula) => {
        formula.price = 0;
        formula.is_paid_with_eatfid_points = true;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories]);

  useEffect(() => {}, [cart]);

  return (
    <PersistGate persistor={persistor} loading={<>Loading...</>}>
      <div
        className={`${classes.container} ${
          alternativeLook && classes.altLook
        } ${
          alternativeLook &&
          alternativeDropdown &&
          displayContent &&
          classes.center
        }`}
        onClick={() => {
          handleClickFunction();
          setDisplayContent(!displayContent);
        }}
      >
        {points > userPoints ? <div className={classes.notBuyable} /> : ""}
        <div className={classes.detailsContainer}>
          <div className={classes.pointsContainer}>
            <CircularProgressbarWithChildren
              value={percentage}
              strokeWidth={5}
              styles={buildStyles({
                pathColor: "#75bb21",
                trailColor: "transparent",
                strokeLinecap: "butt",
              })}
            >
              <div className={classes.pointsDisplay}>
                <h1>{points}</h1>
                <span>
                  {translate(translationsContent, "points", language)}
                </span>
              </div>
            </CircularProgressbarWithChildren>
          </div>
          <div className={classes.category}>
            <p>
              {categories.map(
                (category, i) =>
                  `${
                    i > 0
                      ? ` ${translate(translationsContent, "and2", language)} `
                      : ""
                  }${
                    category.title?.startsWith("4") ||
                    category.title?.startsWith("6")
                      ? translate(translationsContent, "formula", language)
                      : ""
                  } ${
                    category.title?.startsWith("4") ||
                    category.title?.startsWith("6")
                      ? category.formulas[0]?.title
                      : category.title
                  } `
              )}
            </p>
          </div>
        </div>
        <div className={classes.iconContainer}>
          {displayContent ? (
            <BsChevronUp className={classes.icon} />
          ) : (
            <BsChevronDown className={classes.icon} />
          )}
        </div>
      </div>
      {alternativeDropdown && displayContent && (
        <div className={classes.contentContainer}>
          <div className={classes.contentCategories}>
            {categories.map((category, i) => {
              return (
                <>
                  {i > 0 ? (
                    <span className={`${classes.categoryChoice}`}>
                      &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;
                    </span>
                  ) : (
                    ""
                  )}
                  <span
                    key={`selected-category-${category.title}-${i}`}
                    className={`${classes.categoryChoice} ${
                      selectedCategory == i && classes.selected
                    }`}
                    onClick={() => {
                      setSelectedCategory(i);
                    }}
                  >
                    {category.title}
                  </span>
                </>
              );
            })}
          </div>
          <div>
            {categories.map((category, i) => {
              return (
                // eslint-disable-next-line react/jsx-key
                <div
                  key={`selected-content-categry-${category.title}-${i}`}
                  className={`${classes.contentCards} ${
                    selectedCategory == i ? classes.selected : ""
                  }`}
                >
                  <Carousel
                    responsive={carouselResponsive}
                    containerClass={classes.contentCardsScroll}
                    itemClass={classes.carouselItem}
                    autoPlay={false}
                    autoPlaySpeed={1000000}
                    renderButtonGroupOutside={true}
                    customButtonGroup={<ButtonGroup />}
                    draggable
                    pauseOnHover={true}
                  >
                    {category.ingredients.length > 0 &&
                      category.ingredients.map((ingredient, f) => {
                        return (
                          <div key={`category-ingredient-${f}`}>
                            <IngredientCard
                              key={ingredient.id}
                              ingredient={ingredient}
                              onIncrement={() => {
                                if (points <= userPoints) {
                                  dispatch(addItemToCart({ ingredient }));
                                  notify();
                                }
                              }}
                              onDecrement={() => {
                                dispatch(removeItemFromCart(ingredient));
                              }}
                              isEatfid
                              points={ingredient.points}
                              showcase={showcase}
                              hidePrice
                            />
                            {showBone && (
                              <div className={classes.instantPurchaseButton}>
                                <Button
                                  label="GÉNÉRER LE BON"
                                  disabled={
                                    userPoints < (ingredient.points || 0)
                                  }
                                  onClick={() => {
                                    purschaseEatfidInstantly(
                                      ingredient.trivec_code,
                                      ingredient.points ?? 0
                                    );
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </Carousel>
                  <Carousel
                    responsive={carouselResponsive}
                    containerClass={classes.contentCardsScroll}
                    itemClass={classes.carouselItem}
                    autoPlay={false}
                    renderButtonGroupOutside={true}
                    customButtonGroup={<ButtonGroup />}
                    draggable
                  >
                    {category.formulas.length > 0 &&
                      category.formulas.map((formula) => {
                        return (
                          <>
                            <FormulaCard
                              key={formula.id}
                              formula={formula}
                              onIncrement={() => {
                                if (points <= userPoints) {
                                  dispatch(addNewFormulaToCart(formula));
                                  setSelectedFormula(formula);
                                  setCustomFormulaModalVisible(true);
                                  const i = cart.formulaItems.length;
                                  setId(i);
                                }
                              }}
                              onDecrement={() => {
                                dispatch(
                                  removeFormulaFromCart({
                                    formula: formula,
                                    id: formula.id,
                                  })
                                );
                              }}
                              hidePrice
                              isEatfid
                              showcase={showcase}
                              restaurant={restaurant}
                              points={formula.points}
                            />
                            {showBone && (
                              <div className={classes.instantPurchaseButton}>
                                <Button
                                  label="GÉNÉRER LE BON"
                                  disabled={userPoints < points}
                                  onClick={() => {
                                    purschaseEatfidInstantly(
                                      formula.trivec_code,
                                      formula.points ?? 0
                                    );
                                  }}
                                />
                              </div>
                            )}
                          </>
                        );
                      })}
                  </Carousel>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {adelyaPurchaseModalVisible && (
        <DynamicModal
          onClose={() => {
            setAdelyaPurchaseModalVisible(false);
          }}
        >
          <div className={classes.adelyaPurchaseMessageContainer}>
            <p className={classes.adelyaPurchaseMessage}>
              {adelyaPurchaseMessage}
            </p>
          </div>
        </DynamicModal>
      )}
      {restaurant && customFormulaModalVisible && selectedFormula && (
        <CompositionModal
          handleCloseModal={() => {
            handleCustomSaladModalClose();
          }}
          categories={[
            restaurant.main_categories.bases,
            restaurant.main_categories.ingredients,
            restaurant.main_categories.sauces,
          ]}
          sideCategories={restaurant.sides_categories}
          sides={selectedFormula.sides}
          formula={selectedFormula}
          hasDessert={selectedFormula.sides.some(
            (side) => side.slug === "dessert"
          )}
          desserts={restaurant.desserts_categories}
          isEatfid
          id={id}
          custom={false}
        />
      )}
    </PersistGate>
  );
};

export default EatfidCardContainer;
